import { useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney, todayStr } from "../engine/dates";
import { addManualRetainer, deleteManualRetainer, saveRetainerSetting } from "../lib/data";

/**
 * Retainers, as Scoro actually models them: projects with project_type
 * 'retainer', not quotes.
 *
 * Scoro holds no periodic value on a retainer project, so the monthly figure is
 * derived from what has genuinely been invoiced against it — what the client
 * pays, rather than what someone once typed. It can be overridden where the
 * history is misleading.
 *
 * A retainer whose term has ended is never forecast on its own. Several sit "In
 * Progress" with terms that expired, and rolling those forward would invent
 * income from contracts that may not have been renewed. Extending the end date
 * is a decision, and it should look like one.
 */
export default function RetainersView({ retainers, settings, paidInvoices = [], onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    company_name: "",
    monthly_net: "",
    vat_rate: 20,
    term_end: "",
    day_of_month: 1,
  });
  const today = todayStr();

  // The same median the forecast uses, so the tab shows what is actually being
  // applied rather than a second opinion.
  const observedLags = useMemo(() => {
    const lags = {};
    for (const p of paidInvoices) {
      if (!p.company_name || !p.date || !p.paid_date) continue;
      const days = Math.round(
        (new Date(`${p.paid_date}T00:00:00`) - new Date(`${p.date}T00:00:00`)) / 86400000
      );
      if (days < 0 || days > 400) continue;
      (lags[p.company_name] = lags[p.company_name] || []).push(days);
    }
    const out = {};
    for (const [name, list] of Object.entries(lags)) {
      if (list.length < 3) continue;
      list.sort((x, y) => x - y);
      out[name] = list[Math.floor(list.length / 2)];
    }
    return out;
  }, [paidInvoices]);

  const rows = useMemo(() => {
    return retainers
      .map((r) => {
        const cfg = settings[r.project_id] || {};
        const end = cfg.end_override || r.term_end || (r.source === "manual" ? null : null);
        const invoicedTo = cfg.invoiced_to_override || r.invoiced_to;
        // The month after the last one already invoiced — never earlier than
        // now, since a forecast cannot bill backwards.
        const d = invoicedTo ? new Date(`${String(invoicedTo).slice(0, 7)}-01T00:00:00`) : null;
        const nextMonth = d ? new Date(d.getFullYear(), d.getMonth() + 1, 1) : null;
        // Never the current month: this month's retainer invoices are already
        // raised and sitting in the forecast as real receivables.
        const floor = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 1);
        const startsAt = !nextMonth || nextMonth < floor ? floor : nextMonth;
        return {
          ...r,
          cfg,
          end,
          invoicedTo,
          startsAt,
          observedLag: observedLags[r.company_name] ?? null,
          netMonthly:
            Number(
              cfg.monthly_net_override ??
                r.derived_monthly_net ??
                (r.derived_monthly != null && r.vat_observed !== false
                  ? r.derived_monthly / 1.2
                  : r.derived_monthly)
            ) || 0,
          vatRate: cfg.vat_rate != null ? Number(cfg.vat_rate) : r.vat_observed === false ? 0 : 20,
          monthly: Number(cfg.monthly_override ?? r.derived_monthly) || 0,
          // A typed-in retainer with no end date is ongoing, not expired.
          expired: end ? end < today : r.source !== "manual",
          included: cfg.include !== false,
        };
      })
      .map((r) => ({ ...r, gross: Math.round(r.netMonthly * (1 + r.vatRate / 100) * 100) / 100 }))
      .sort((a, b) => Number(a.expired) - Number(b.expired) || b.gross - a.gross);
  }, [retainers, settings, today, observedLags]);

  const live = rows.filter((r) => !r.expired && r.included);
  const expired = rows.filter((r) => r.expired);
  const monthlyTotal = live.reduce((s, r) => s + r.gross, 0);

  const save = async (id, patch) => {
    setBusy(id);
    setError(null);
    try {
      await saveRetainerSetting(id, patch);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Retainers</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 700 }}>
        Taken from Scoro projects marked as retainers. Scoro holds no monthly value on them, so the
        figure is worked out from what has actually been invoiced against each one — correct it
        where the history misleads. Amounts are gross, and repeat monthly until the term ends.
      </p>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 700 }}>
        Each one is forecast under its <strong>client</strong>, not as a line of its own, and starts
        the month after the last invoice actually raised against it — September is already in the
        forecast as real invoices, so repeating it here would count the same money twice. The cash
        lands not on the billing day but after the client&rsquo;s usual time to pay, worked out from
        their own settled invoices.
      </p>

      <div style={{ display: "flex", gap: 12, margin: "14px 0", flexWrap: "wrap" }}>
        <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
            In the forecast
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 24 }}>{fmtMoney(monthlyTotal)}</div>
          <div style={{ fontSize: 11, color: T.muted }}>a month · {live.length} retainers</div>
        </div>
        {expired.length > 0 && (
          <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px", border: `1px solid ${T.warn}` }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
              Term ended
            </div>
            <div style={{ fontFamily: fontDisplay, fontSize: 24, color: T.warn }}>
              {fmtMoney(expired.reduce((s, r) => s + r.gross, 0))}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              a month · {expired.length} not being forecast
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <button
          onClick={() => setAdding((v) => !v)}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "none",
            background: T.accent,
            color: "#fff",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {adding ? "Cancel" : "Add a retainer"}
        </button>
      </div>

      {adding && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 11, color: T.muted }}>
              Client
              <br />
              <input
                value={draft.company_name}
                onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
                placeholder="Nielsen-Massey Vanillas International B.V."
                style={{ fontSize: 12, padding: "5px 7px", width: 260, marginTop: 2 }}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Fee a month, excl. VAT
              <br />
              <input
                value={draft.monthly_net}
                onChange={(e) => setDraft({ ...draft, monthly_net: e.target.value })}
                placeholder="4000"
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", width: 96, marginTop: 2 }}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              VAT
              <br />
              <select
                value={draft.vat_rate}
                onChange={(e) => setDraft({ ...draft, vat_rate: Number(e.target.value) })}
                style={{ fontSize: 12, padding: "5px 7px", marginTop: 2, fontFamily: "inherit" }}
              >
                <option value={20}>20%</option>
                <option value={0}>none · outside the UK</option>
              </select>
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Billed on
              <br />
              <input
                type="number"
                min="1"
                max="28"
                value={draft.day_of_month}
                onChange={(e) => setDraft({ ...draft, day_of_month: e.target.value })}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", width: 56, marginTop: 2 }}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Runs until
              <br />
              <input
                type="date"
                value={draft.term_end}
                onChange={(e) => setDraft({ ...draft, term_end: e.target.value })}
                style={{ fontFamily: fontMono, fontSize: 12, padding: "5px 7px", marginTop: 2 }}
              />
            </label>
            <button
              disabled={busy === "new" || !draft.company_name.trim() || !Number(draft.monthly_net)}
              onClick={async () => {
                setBusy("new");
                setError(null);
                try {
                  await addManualRetainer(draft);
                  setAdding(false);
                  setDraft({ company_name: "", monthly_net: "", vat_rate: 20, term_end: "", day_of_month: 1 });
                  await onChanged();
                } catch (e) {
                  setError(e.message || String(e));
                } finally {
                  setBusy(null);
                }
              }}
              style={{
                marginLeft: "auto",
                padding: "7px 14px",
                borderRadius: 6,
                border: "none",
                background:
                  draft.company_name.trim() && Number(draft.monthly_net) ? T.accent : T.border,
                color: draft.company_name.trim() && Number(draft.monthly_net) ? "#fff" : T.muted,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {busy === "new" ? "Adding…" : "Add"}
            </button>
          </div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
            For a client who pays monthly without a Scoro project behind it. It sits alongside the
            synced ones and survives the next sync.
          </div>
        </div>
      )}

      {expired.length > 0 && (
        <div style={{ fontSize: 12, color: T.ink, background: "#FBEFE2", border: `1px solid ${T.warn}`, borderRadius: 8, padding: 12, marginBottom: 14, lineHeight: 1.55 }}>
          <strong>{expired.length} retainers are marked in progress but their term has ended.</strong>{" "}
          They are not in the forecast, because renewing is a decision rather than something to
          assume. If they have been renewed, extend the end date and they come back at their derived
          value.
        </div>
      )}

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>In</th>
              <th style={th}>Retainer</th>
              <th style={{ ...th, textAlign: "right" }}>Fee excl. VAT</th>
              <th style={th}>VAT</th>
              <th style={{ ...th, textAlign: "right" }}>Billed</th>
              <th style={th}>Derived from</th>
              <th style={th}>Forecast from</th>
              <th style={th}>Paid after</th>
              <th style={th}>Term ends</th>
              <th style={th}>Billed on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.project_id}
                style={{
                  borderTop: `1px solid ${T.borderSoft}`,
                  background: r.expired ? "#FBEFE2" : undefined,
                  opacity: r.included ? 1 : 0.55,
                }}
              >
                <td style={{ padding: "6px 10px" }}>
                  <input
                    type="checkbox"
                    checked={r.included}
                    disabled={busy === r.project_id}
                    title="Include in the forecast"
                    onChange={(e) => save(r.project_id, { include: e.target.checked })}
                  />
                </td>
                <td style={{ padding: "6px 10px", fontSize: 12 }}>
                  {r.company_name || r.name}
                  <div style={{ fontSize: 10, color: T.muted }}>{r.name}</div>
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>
                  <input
                    key={`m-${r.project_id}-${r.netMonthly}`}
                    defaultValue={Math.round(r.netMonthly)}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v && Math.abs(v - r.netMonthly) > 0.5)
                        save(r.project_id, { monthly_net_override: v });
                    }}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 12,
                      padding: "3px 5px",
                      width: 84,
                      textAlign: "right",
                      border: `1px solid ${r.cfg.monthly_net_override != null ? T.accent : T.border}`,
                      background: r.cfg.monthly_net_override != null ? T.accentSoft : "transparent",
                      borderRadius: 4,
                    }}
                  />
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <select
                    value={r.vatRate}
                    onChange={(e) => save(r.project_id, { vat_rate: Number(e.target.value) })}
                    style={{
                      fontFamily: "inherit",
                      fontSize: 11,
                      padding: "3px 5px",
                      borderRadius: 4,
                      border: `1px solid ${r.cfg.vat_rate != null ? T.accent : T.border}`,
                      background: r.cfg.vat_rate != null ? T.accentSoft : "transparent",
                    }}
                  >
                    <option value={20}>20%</option>
                    <option value={0}>none · outside the UK</option>
                  </select>
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "right",
                    fontFamily: fontMono,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {fmtMoney(r.gross)}
                </td>
                <td style={{ padding: "6px 10px", fontSize: 10, color: T.muted }}>
                  {r.cfg.monthly_override != null
                    ? "set by hand"
                    : r.months_observed
                      ? `${r.invoice_count} invoices over ${r.months_observed} months`
                      : "no invoice history"}
                </td>
                <td style={{ padding: "6px 10px", fontSize: 11 }}>
                  <div style={{ fontFamily: fontMono }}>
                    {r.startsAt.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted }}>
                    {r.invoicedTo
                      ? `invoiced to ${new Date(`${String(r.invoicedTo).slice(0, 7)}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`
                      : "no invoices raised"}
                  </div>
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <input
                    type="number"
                    min="0"
                    max="180"
                    key={`l-${r.project_id}-${r.cfg.payment_lag_days ?? "auto"}`}
                    defaultValue={r.cfg.payment_lag_days ?? ""}
                    placeholder={String(r.observedLag ?? 30)}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const v = raw === "" ? null : Math.min(Math.max(Number(raw) || 0, 0), 180);
                      if (v !== (r.cfg.payment_lag_days ?? null)) save(r.project_id, { payment_lag_days: v });
                    }}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 11,
                      padding: "3px 5px",
                      width: 52,
                      border: `1px solid ${r.cfg.payment_lag_days != null ? T.accent : T.border}`,
                      background: r.cfg.payment_lag_days != null ? T.accentSoft : "transparent",
                      borderRadius: 4,
                    }}
                  />
                  <span style={{ fontSize: 10, color: T.muted }}> days</span>
                  <div style={{ fontSize: 9, color: T.muted }}>
                    {r.cfg.payment_lag_days != null
                      ? "set by hand"
                      : r.observedLag != null
                        ? "their usual"
                        : "assumed"}
                  </div>
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <input
                    type="date"
                    key={`e-${r.project_id}-${r.end}`}
                    defaultValue={r.end || ""}
                    min="2000-01-01"
                    max="2099-12-31"
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v && v !== r.end && /^(20|21)\d{2}-\d{2}-\d{2}$/.test(v)) {
                        save(r.project_id, { end_override: v });
                      }
                    }}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 11,
                      padding: "3px 5px",
                      border: `1px solid ${r.cfg.end_override ? T.accent : r.expired ? T.warn : T.border}`,
                      background: r.cfg.end_override ? T.accentSoft : "transparent",
                      borderRadius: 4,
                    }}
                  />
                  {r.expired && (
                    <div style={{ fontSize: 9, color: T.warn, fontWeight: 700 }}>ENDED</div>
                  )}
                </td>
                <td style={{ padding: "6px 10px" }}>
                  {r.source === "manual" && (
                    <button
                      title="Remove this retainer"
                      onClick={async () => {
                        setBusy(r.project_id);
                        try {
                          await deleteManualRetainer(r.project_id);
                          await onChanged();
                        } catch (e) {
                          setError(e.message || String(e));
                        } finally {
                          setBusy(null);
                        }
                      }}
                      style={{
                        border: "none",
                        background: "none",
                        color: T.muted,
                        cursor: "pointer",
                        fontSize: 13,
                        float: "right",
                      }}
                    >
                      ×
                    </button>
                  )}
                  <input
                    type="number"
                    min="1"
                    max="28"
                    key={`d-${r.project_id}-${r.cfg.day_of_month || 1}`}
                    defaultValue={r.cfg.day_of_month || 1}
                    onBlur={(e) => {
                      const v = Math.min(Math.max(Number(e.target.value) || 1, 1), 28);
                      if (v !== (r.cfg.day_of_month || 1)) save(r.project_id, { day_of_month: v });
                    }}
                    style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px", width: 46 }}
                  />
                  <span style={{ fontSize: 10, color: T.muted }}> of the month</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && (
        <div style={{ fontSize: 12, color: T.muted, padding: 12 }}>
          No retainer projects found in Scoro.
        </div>
      )}
      <div style={{ fontSize: 11, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
        Term dates come from Scoro. Changing one here overrides it for the forecast only — it does
        not write back, so Scoro stays the record.
      </div>
    </div>
  );
}
